import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Observable, of, forkJoin } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { AuthService } from '../../services/auth/auth.service';
import { AlertsService } from '../../services/alerts/alerts.service';
import { InvestmentsSummaryService } from '../../services/investments-summary/investments-summary.service';
import { ROUTE_PATHS } from '../../utils/route-paths.const';

/**
 * Auth Guard
 * Protects routes that require authentication
 * Fetches user profile, alerts, and the investments summary to ensure fresh
 * data for downstream guards
 * Redirects to phone verification if not authenticated
 *
 * Bypass: Pass state: { bypassGuard: true } to skip fetches when guard redirects internally
 * Example: router.navigate([path], { state: { bypassGuard: true } })
 */
export const authGuard: CanActivateFn = (): Observable<boolean> => {
  const authService = inject(AuthService);
  const alertsService = inject(AlertsService);
  const investmentsSummaryService = inject(InvestmentsSummaryService);
  const router = inject(Router);

  if (!authService.isAuthenticated()) {
    authService.clearAuthData();
    router.navigate([ROUTE_PATHS.AUTH, ROUTE_PATHS.PHONE_VERIFICATION], {
      replaceUrl: true,
    });
    return of(false);
  }

  // Bypass guard to avoid duplicate fetches when child guards redirect internally
  const navigation = router.currentNavigation();
  const bypassGuard = navigation?.extras?.state?.['bypassGuard'];

  if (bypassGuard) {
    return of(true);
  }

  // Fetch profile, alerts, and the investments summary in parallel to update
  // signals before child guards run
  return forkJoin([
    authService.getProfile().pipe(catchError(() => of(null))),
    alertsService.fetchAlerts().pipe(catchError(() => of(null))),
    investmentsSummaryService.fetchSummary().pipe(catchError(() => of(null))),
  ]).pipe(
    map(() => true),
    catchError(() => of(true))
  );
};
